import { useCallback, useEffect, useRef, useState } from 'react'
import { platformStoreSet } from '../../../../shared/helpers'
import { pollLocalizationAuthStatus } from '../../actions/contentActions'
import type {
  LocalizationAuthCache,
  LocalizationAuthInitialData,
  LocalizationAuthPollResponse,
  UseLocalizePollResult,
} from './types'
import { LOCALIZATION_AUTH_CACHE_KEY, LocalizationAuthStatus } from './types'

export const useLocalizePoll = (
  initialData: LocalizationAuthInitialData
): UseLocalizePollResult => {
  const [data, setData] = useState<LocalizationAuthPollResponse | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [isPolling, setIsPolling] = useState(true)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const attemptCountRef = useRef(0)
  const codeExpirationTimestampRef = useRef<number | null>(null)

  useEffect(() => {
    const expiresInSeconds = Number(initialData.expires_in ?? 0)
    if (expiresInSeconds > 0) {
      codeExpirationTimestampRef.current =
        Math.floor(Date.now() / 1000) + expiresInSeconds
    } else {
      codeExpirationTimestampRef.current = null
    }
  }, [initialData.code, initialData.expires_in])

  const isAuthorizationExpired = useCallback((): boolean => {
    if (!codeExpirationTimestampRef.current) {
      return false
    }

    const currentTimestamp = Math.floor(Date.now() / 1000)
    return currentTimestamp >= codeExpirationTimestampRef.current
  }, [])

  const maxAttempts = (() => {
    const expiresIn = initialData.expires_in ?? 0
    const pollInterval = initialData.code_status_poll_interval ?? 1

    if (pollInterval === 0 || expiresIn <= 0 || pollInterval < 0) {
      return 1
    }

    const calculated = Math.floor(expiresIn / pollInterval)
    return isNaN(calculated) || calculated <= 0 ? 1 : calculated
  })()

  useEffect(() => {
    attemptCountRef.current = 0
    setIsPolling(true)
    setError(null)
    setData(null)

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setIsPolling(false)
    }

    const pollEndpoint = async () => {
      try {
        const result: LocalizationAuthPollResponse =
          await pollLocalizationAuthStatus(initialData.code)

        setData(result)

        if (result.status !== LocalizationAuthStatus.WAITING) {
          stopPolling()
          if (result.status === LocalizationAuthStatus.AUTHORIZED) {
            const sixMonthsFromNow =
              Math.floor(Date.now() / 1000) + 182 * 24 * 60 * 60
            const cacheData = JSON.stringify({
              timestamp: Math.floor(Date.now() / 1000),
              expiresAt: sixMonthsFromNow,
              status: LocalizationAuthStatus.AUTHORIZED,
              station_id: result.station_id,
            } as LocalizationAuthCache)
            platformStoreSet(LOCALIZATION_AUTH_CACHE_KEY, cacheData)
          } else if (
            result.status === LocalizationAuthStatus.UNAUTHORIZED ||
            result.status === LocalizationAuthStatus.ERROR
          ) {
            setError(new Error(`Status returned: ${result.status}`))
          }
          return
        }

        attemptCountRef.current += 1

        if (attemptCountRef.current >= maxAttempts) {
          stopPolling()
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred during polling')
        )
        stopPolling()
      }
    }

    pollEndpoint()

    intervalRef.current = setInterval(() => {
      pollEndpoint()
    }, initialData.code_status_poll_interval * 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setIsPolling(false)
    }
  }, [initialData.code, initialData.code_status_poll_interval, maxAttempts])

  return { data, error, isPolling, isAuthorizationExpired }
}
